/**
 * SurveyFindings — Finding entry form + list.
 * Route: /visits/:visitId/survey/findings
 */

import { useState, useEffect, useCallback } from 'react'
import { useParams, Link } from 'react-router-dom'
import { ArrowLeft, Plus, Sparkles } from 'lucide-react'
import { z } from 'zod'
import { AreaSelector } from '../../components/survey/AreaSelector'
import { PhotoCapture } from '../../components/survey/PhotoCapture'
import { VoiceRecorder } from '../../components/survey/VoiceRecorder'
import { FindingCard } from '../../components/survey/FindingCard'
import { SyncStatus } from '../../components/survey/SyncStatus'
import { FieldAppShell } from '../../components/survey/FieldAppShell'
import { surveyStore, type PendingFinding } from '../../lib/offline/surveyStore'
import { enqueueFinding } from '../../lib/services/surveySync'
import {
  SURVEY_DOMAINS,
  SEVERITY_LEVELS,
  type FacilityArea,
  type SurveyDomain,
  type SeverityLevel,
  type SurveyFinding,
  type SurveySession,
} from '../../lib/services/survey'
import { phiSafeText } from '../../lib/validation'
import { invokeFunction } from '../../lib/api'

const FindingSchema = z.object({
  domain: z.enum(SURVEY_DOMAINS as unknown as [string, ...string[]]),
  standard_ref: z.string().min(1, 'Standard reference required'),
  severity: z.number().min(1).max(3),
  observation: z.string().min(1, 'Observation required'),
  area: z.string().min(1, 'Area required'),
})

export function SurveyFindings() {
  const { visitId } = useParams<{ visitId: string }>()
  const [session, setSession] = useState<SurveySession | null>(null)
  const [findings, setFindings] = useState<PendingFinding[]>([])
  const [showForm, setShowForm] = useState(false)

  // Form state
  const [domain, setDomain] = useState<SurveyDomain>('ENV')
  const [standardRef, setStandardRef] = useState('')
  const [severity, setSeverity] = useState<SeverityLevel>(2)
  const [observation, setObservation] = useState('')
  const [area, setArea] = useState<FacilityArea | null>(null)
  const [photoIds, setPhotoIds] = useState<string[]>([])
  const [phiWarning, setPhiWarning] = useState<string | null>(null)
  const [validationErrors, setValidationErrors] = useState<string[]>([])
  const [isSaving, setIsSaving] = useState(false)
  const [isAiDrafting, setIsAiDrafting] = useState(false)

  useEffect(() => {
    if (!visitId) return
    surveyStore.getSessionByVisit(visitId).then((s) => setSession(s ?? null))
  }, [visitId])

  useEffect(() => {
    if (!session) return
    surveyStore.getFindingsBySurvey(session.survey_id).then(setFindings)
  }, [session])

  // PHI check on observation text
  useEffect(() => {
    if (!observation) { setPhiWarning(null); return }
    const result = phiSafeText.safeParse(observation)
    setPhiWarning(result.success ? null : result.error.issues[0].message)
  }, [observation])

  const resetForm = () => {
    setDomain('ENV')
    setStandardRef('')
    setSeverity(2)
    setObservation('')
    setArea(null)
    setPhotoIds([])
    setValidationErrors([])
    setShowForm(false)
  }

  const handleAiDraft = useCallback(async () => {
    if (!visitId || !standardRef.trim()) return
    setIsAiDrafting(true)
    try {
      const severityLabel = severity === 1 ? 'low' : severity === 2 ? 'medium' : 'high'
      const { data, error } = await invokeFunction<{ summary?: string }>('gpt-assist', {
        method: 'POST',
        path: `/visits/${visitId}/findings:draft-with-ai`,
        body: {
          standard_id: standardRef.trim(),
          severity: severityLabel,
          notes: observation || undefined,
        },
      })
      if (error) {
        setValidationErrors([`AI draft failed: ${error.message}`])
      } else if (data?.summary) {
        setObservation(data.summary.replace(/^DRAFT:\s*/i, ''))
      }
    } catch {
      setValidationErrors(['AI draft unavailable'])
    } finally {
      setIsAiDrafting(false)
    }
  }, [visitId, standardRef, severity, observation])

  const handleSave = useCallback(async () => {
    const result = FindingSchema.safeParse({ domain, standard_ref: standardRef, severity, observation, area })
    if (!result.success) {
      setValidationErrors(result.error.issues.map((i) => i.message))
      return
    }
    if (!session || !visitId) return

    setIsSaving(true)
    const finding: PendingFinding = {
      finding_id: crypto.randomUUID(),
      survey_id: session.survey_id,
      visit_id: visitId,
      domain,
      standard_ref: standardRef,
      severity,
      observation: observation.trim(),
      area: area!,
      photo_ids: photoIds,
      audio_transcript: null,
      created_at: new Date().toISOString(),
    }

    await surveyStore.saveFinding(finding)
    await enqueueFinding(visitId, session.survey_id, finding.finding_id)
    setFindings((prev) => [finding, ...prev])
    resetForm()
    setIsSaving(false)
  }, [domain, standardRef, severity, observation, area, photoIds, session, visitId])

  if (!visitId || !session) {
    return (
      <FieldAppShell eyebrow="Survey" title="Findings" backTo=".." backLabel="Back">
        <div className="px-4 py-6">
          <p className="text-sm text-slate-500">No active survey.</p>
          <Link to=".." className="btn-ghost text-sm mt-4 inline-block">Back</Link>
        </div>
      </FieldAppShell>
    )
  }

  return (
    <FieldAppShell eyebrow="Survey" title="Findings" backTo=".." backLabel="Back" rightSlot={<SyncStatus />}>
    <div className="space-y-4 px-4 py-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Link to=".." className="text-slate-400 hover:text-slate-600">
            <ArrowLeft size={20} />
          </Link>
          <h2 className="section-title">Findings</h2>
        </div>
        <SyncStatus />
      </div>

      {/* Add button */}
      {!showForm && (
        <button
          type="button"
          onClick={() => setShowForm(true)}
          className="btn-primary w-full"
        >
          <Plus size={16} className="inline mr-2" /> New Finding
        </button>
      )}

      {/* Entry form */}
      {showForm && (
        <div className="app-surface p-4 space-y-3">
          <span className="section-title text-base">New Finding</span>

          <AreaSelector value={area} onChange={setArea} />

          <div className="flex items-center gap-2">
            <select
              value={domain}
              onChange={(e) => setDomain(e.target.value as SurveyDomain)}
              className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:border-[var(--brand-primary)] focus:outline-none focus:ring-2 focus:ring-blue-100"
            >
              {SURVEY_DOMAINS.map((d) => (
                <option key={d} value={d}>{d}</option>
              ))}
            </select>

            <select
              value={severity}
              onChange={(e) => setSeverity(Number(e.target.value) as SeverityLevel)}
              className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:border-[var(--brand-primary)] focus:outline-none focus:ring-2 focus:ring-blue-100"
            >
              {SEVERITY_LEVELS.map((s) => (
                <option key={s} value={s}>Severity {s}</option>
              ))}
            </select>
          </div>

          <input
            type="text"
            placeholder="Standard reference (e.g. IC.02.02.01)"
            value={standardRef}
            onChange={(e) => setStandardRef(e.target.value)}
            className="block w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:border-[var(--brand-primary)] focus:outline-none focus:ring-2 focus:ring-blue-100"
          />

          <textarea
            placeholder="Observation…"
            value={observation}
            onChange={(e) => setObservation(e.target.value)}
            rows={4}
            className="block w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:border-[var(--brand-primary)] focus:outline-none focus:ring-2 focus:ring-blue-100 resize-none"
          />

          {phiWarning && (
            <p className="text-xs font-medium text-amber-600 bg-amber-50 p-2 rounded">{phiWarning}</p>
          )}

          <div className="flex items-center gap-2">
            {area && (
              <PhotoCapture
                visitId={visitId}
                surveyId={session.survey_id}
                area={area}
                onCapture={(photo) => setPhotoIds((prev) => [...prev, photo.photo_id])}
              />
            )}
            <VoiceRecorder
              onTranscript={(r) => setObservation((prev) => prev ? `${prev}\n${r.text}` : r.text)}
            />
          </div>

          {photoIds.length > 0 && (
            <p className="text-xs text-slate-500">{photoIds.length} photo(s) attached</p>
          )}

          {validationErrors.length > 0 && (
            <div className="text-xs text-red-600 space-y-1">
              {validationErrors.map((e, i) => <p key={i}>{e}</p>)}
            </div>
          )}

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleAiDraft}
              disabled={isAiDrafting || !standardRef.trim()}
              className="rounded-lg border border-purple-300 bg-purple-50 px-3 py-2.5 text-sm font-medium text-purple-700 hover:bg-purple-100 disabled:opacity-50"
              title="Generate AI draft observation"
            >
              <Sparkles size={14} className="inline mr-1" />
              {isAiDrafting ? 'Drafting…' : 'AI Draft'}
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={isSaving}
              className="btn-primary flex-1 py-2.5 text-sm disabled:opacity-60"
            >
              {isSaving ? 'Saving…' : 'Save'}
            </button>
            <button
              type="button"
              onClick={resetForm}
              className="btn-ghost flex-1 py-2.5 text-sm"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Findings list */}
      <div className="space-y-2">
        {findings.map((f) => (
          <FindingCard
            key={f.finding_id}
            finding={f as unknown as SurveyFinding}
          />
        ))}
        {findings.length === 0 && !showForm && (
          <p className="text-center text-sm text-slate-400 py-8">No findings yet.</p>
        )}
      </div>
    </div>
    </FieldAppShell>
  )
}
